import React, { useContext, useState, useEffect, useRef } from 'react';
import { 
  Check, 
  X, 
  Sparkles, 
  Zap, 
  Crown, 
  Shield, 
  TrendingUp, 
  BarChart, 
  CreditCard, 
  Globe, 
  Clock, 
  Users,
  ChevronRight,
  Loader
} from 'lucide-react';

import { updateVendeurInfo } from '../../db/users/updateVendeurInfo';
import AppContext from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { UserKeysService } from '../../db/Aggregator/UserKeysService';
import TestPlanAlert from '../espace/TestPlanAlert';

export default function Plans() {
  const { id } = useContext(AppContext);
  const [showAlert, setShowAlert] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('gratuit');
  const [animateCards, setAnimateCards] = useState(false);
  const [isIdMissing, setIsIdMissing] = useState(false);
  const navigate = useNavigate();
  // Référence pour éviter la redirection en boucle
  const redirectDone = useRef(false);
  
  // Vérifier si l'ID est disponible - version simplifiée
  useEffect(() => {
    console.log("Plans: Composant affiché avec ID:", id || "non disponible");
    
    if (!id) {
      setIsIdMissing(true);
    } else {
      setIsIdMissing(false);
      
      // Marquer la visite de la page Plans dans la session
      sessionStorage.setItem('hasVisitedPlansPage', 'true');
    }
    
    // Animation des cartes
    setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    
  }, [id]);
  
  // Effet pour l'animation des cartes et vérification des données utilisateur
  useEffect(() => {
    console.log("Plans: Vérification du statut actuel de l'utilisateur:", id);
    
    // Activer l'animation des cartes
    setTimeout(() => {
      setAnimateCards(true);
    }, 100);
    
    // Si nous sommes redirigés vers Plans, c'est probablement parce que l'utilisateur
    // n'a pas encore de plan, mais vérifions quand même les données
    if (id) {
      // Marquer que nous avons visité la page des plans
      sessionStorage.setItem('hasVisitedPlansPage', 'true');
    }
  }, [id]);
  
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };
  
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // Fonction de mise à jour du plan utilisateur
  const updateVendeur = async (userId, plan) => {
    console.log("Plan sélectionné:", plan);
    
    // Montrer l'alerte pour le plan gratuit
    if (plan === "gratuit") {
      setShowAlert(true);
      return;
    }
    
    // Rediriger vers la page de paiement pour le plan payant
    if (plan === "payant") {
      navigate("/PaymentSelector");
      return;
    }
    
    // Commencer la mise à jour
    setIsUpdating(true);
    setUpdateError(null);
    
    try {
      if (!userId) {
        throw new Error("ID utilisateur manquant");
      }
      
      // Préparer les données à mettre à jour
      const updateData = {
        plan: true,          // Indique que l'utilisateur a choisi un plan
        type_plan: plan,     // Le type de plan choisi (gratuit, payant)
        deja: true,          // Indique que l'utilisateur a déjà passé l'onboarding
        updated_at: new Date().toISOString()
      };
      
      console.log("Mise à jour des données utilisateur:", updateData);
      
      // Écrire les données en base
      await updateVendeurInfo(userId, updateData);
      console.log("✅ Plan utilisateur mis à jour avec succès:", plan);
      
      // Nettoyer le sessionStorage pour éviter des problèmes de redirection
      sessionStorage.removeItem('initialRedirectionDone');
      
      // Marquer que le plan a été choisi (pour référence future)
      localStorage.setItem('planChosen', plan);
      localStorage.setItem('planChosenTime', new Date().toISOString());
      
      // Rediriger vers l'espace principal
      console.log("➡️ Redirection vers l'espace utilisateur");
      navigate("/IndexEspace");
    } catch (error) {
      console.error("❌ Erreur lors de la mise à jour du plan:", error);
      setUpdateError("Erreur lors de la mise à jour: " + (error.message || "erreur inconnue"));
    } finally {
      setIsUpdating(false);
    }
  };

  // Comparaison des fonctionnalités
  const features = [
    { name: "Solutions de paiement", free: "Basiques", premium: "Personnalisées" },
    { name: "Frais par transaction", free: "18%", premium: "0%" },
    { name: "Fréquence de retrait", free: "Hebdomadaire", premium: "Instantané" },
    { name: "Assistance client", free: "Standard", premium: "Prioritaire" },
    { name: "Nombre de groupes", free: "3 max", premium: "Illimité" },
    { name: "Statistiques avancées", free: "Limitées", premium: "Complètes" },
    { name: "API Développeur", free: "Non", premium: "Oui" },
    { name: "Configuration personnalisée", free: "Non", premium: "Oui" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-10 px-4 sm:px-6">
      {/* Header section avec animation */}
      <div className="text-center space-y-6 mb-12 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-blue-500 rounded-full filter blur-3xl opacity-10 -z-10"></div>
        
        <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Choisissez votre plan
        </h1>
        
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Sélectionnez le forfait qui correspond le mieux à vos besoins et commencez à monétiser vos groupes Telegram dès aujourd'hui.
        </p>
        
        {/* Affichage du mode test */}
        {showAlert && <TestPlanAlert onClose={() => setShowAlert(false)} />}
        
        {/* Affichage des erreurs */}
        {updateError && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm max-w-md mx-auto">
            {updateError}
            <button 
              onClick={() => setUpdateError(null)} 
              className="ml-2 text-red-800 hover:text-red-900"
            >
              ×
            </button>
          </div>
        )}
        
        {isIdMissing && (
          <div className="bg-yellow-50 text-yellow-700 p-3 rounded-lg mb-4 text-sm max-w-md mx-auto">
            Une erreur est survenue lors de la récupération de vos informations. Veuillez vous reconnecter.
            <button 
              onClick={() => navigate('/')} 
              className="ml-2 underline text-yellow-800"
            >
              Retour à l'accueil
            </button>
          </div>
        )}
        
        {/* Pills selector */}
        <div className="inline-flex items-center p-1 bg-gray-100 rounded-full mx-auto shadow-inner">
          <button
            onClick={() => handlePlanSelect('gratuit')}
            className={`px-6 py-2 rounded-full transition-all duration-300 font-medium ${
              selectedPlan === 'gratuit' 
                ? 'bg-white text-blue-600 shadow-md' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Gratuit
          </button>
          <button
            onClick={() => handlePlanSelect('payant')}
            className={`px-6 py-2 rounded-full transition-all duration-300 font-medium ${
              selectedPlan === 'payant' 
                ? 'bg-white text-blue-600 shadow-md' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Premium
          </button>
        </div>
      </div>

      {/* Plans cards avec animations */}
      <div className="max-w-7xl mx-auto mb-16">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Plan Gratuit */}
          <div 
            className={`rounded-3xl bg-white p-8 border border-gray-100 shadow-xl hover:shadow-2xl transition-all duration-500 
              ${animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              ${selectedPlan === 'gratuit' ? 'ring-2 ring-blue-400' : ''}
            `}
            style={{ 
              transitionDelay: '0.1s',
              transformStyle: 'preserve-3d', 
              perspective: '1000px' 
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-blue-100 rounded-2xl p-3 mr-4">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Gratuit</h3>
                  <p className="text-gray-500">Pour commencer</p>
                </div>
              </div>
              <div className="text-3xl font-bold bg-blue-50 text-blue-600 px-4 py-2 rounded-xl">
                0€
              </div>
            </div>
            
            <div className="mb-8">
              <div className="border-b pb-4 mb-4">
                <p className="text-gray-500">
                  Parfait pour les débutants qui veulent tester la plateforme et commencer à monétiser leur contenu sans investissement initial.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-x-3">
                  <Users className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Jusqu'à 3 groupes</span>
                </div>
                <div className="flex items-center gap-x-3">
                  <CreditCard className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Paiements intégrés</span>
                </div>
                <div className="flex items-center gap-x-3">
                  <BarChart className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Statistiques basiques</span>
                </div>
                <div className="flex items-center gap-x-3">
                  <Clock className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">Retraits hebdomadaires</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => updateVendeur(id, "gratuit")}
              disabled={isUpdating}
              className={`w-full py-4 px-6 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-lg shadow-lg hover:shadow-blue-200 transition-all duration-300 transform hover:-translate-y-1 flex justify-center items-center ${isUpdating ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isUpdating ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  Commencer gratuitement
                  <ChevronRight className="ml-2 w-5 h-5" />
                </>
              )}
            </button>
          </div>
          
          {/* Plan Premium */}
          <div 
            className={`rounded-3xl p-8 border-0 shadow-xl hover:shadow-2xl transition-all duration-500 relative overflow-hidden
              ${animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
              ${selectedPlan === 'payant' ? 'ring-2 ring-purple-400' : ''}
            `}
            style={{ 
              transitionDelay: '0.2s',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              transformStyle: 'preserve-3d', 
              perspective: '1000px' 
            }}
          >
            {/* Badge populaire */}
            <div className="absolute top-0 right-0">
              <div className="bg-yellow-400 text-blue-900 text-xs font-bold uppercase py-1 px-3 rounded-bl-lg rounded-tr-lg">
                Populaire
              </div>
            </div>
            
            {/* Arrière-plan décoratif */}
            <div className="absolute -right-20 -top-20 w-64 h-64 bg-white opacity-10 rounded-full"></div>
            <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-white opacity-10 rounded-full"></div>
            
            <div className="flex items-center justify-between mb-6 relative z-10">
              <div className="flex items-center">
                <div className="bg-white/20 backdrop-blur-md rounded-2xl p-3 mr-4">
                  <Crown className="w-6 h-6 text-yellow-300" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">Premium</h3>
                  <p className="text-gray-100">Pour les professionnels</p>
                </div>
              </div>
              <div className="text-white">
                <span className="text-3xl font-bold">50€</span>
                <span className="text-sm">/mois</span>
              </div>
            </div>
            
            <div className="mb-8 relative z-10">
              <div className="border-b border-white/20 pb-4 mb-4">
                <p className="text-white/80">
                  Solution complète pour les créateurs qui souhaitent maximiser leurs revenus et offrir une expérience premium à leur communauté.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-x-3">
                  <Sparkles className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                  <span className="text-white font-medium">Groupes illimités</span>
                </div>
                <div className="flex items-center gap-x-3">
                  <Shield className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                  <span className="text-white font-medium">0% de frais par transaction</span>
                </div>
                <div className="flex items-center gap-x-3">
                  <TrendingUp className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                  <span className="text-white font-medium">Statistiques avancées</span>
                </div>
                <div className="flex items-center gap-x-3">
                  <Globe className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                  <span className="text-white font-medium">Retraits instantanés</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => updateVendeur(id, "payant")}
              disabled={isUpdating}
              className={`w-full py-4 px-6 rounded-xl bg-white text-indigo-700 font-bold text-lg shadow-lg hover:shadow-indigo-900/20 transition-all duration-300 transform hover:-translate-y-1 relative z-10 flex justify-center items-center ${isUpdating ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isUpdating ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin text-indigo-700" />
                  Chargement...
                </>
              ) : (
                <>
                  Passer à Premium
                  <ChevronRight className="ml-2 w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Tableau comparatif */}
      <div className="max-w-4xl mx-auto mb-20">
        <h2 className="text-2xl font-bold text-center mb-8">Comparaison détaillée</h2>
        
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-4 px-6 text-left text-gray-600 font-semibold">Fonctionnalités</th>
                <th className="py-4 px-6 text-center text-gray-600 font-semibold">Gratuit</th>
                <th className="py-4 px-6 text-center text-gray-600 font-semibold">Premium</th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="py-3 px-6 text-gray-700">{feature.name}</td>
                  <td className="py-3 px-6 text-center">
                    {feature.free === "Non" ? (
                      <X className="w-5 h-5 text-gray-400 mx-auto" />
                    ) : feature.free === "Oui" ? (
                      <Check className="w-5 h-5 text-blue-500 mx-auto" />
                    ) : (
                      <span className="text-gray-600">{feature.free}</span>
                    )}
                  </td>
                  <td className="py-3 px-6 text-center">
                    {feature.premium === "Non" ? (
                      <X className="w-5 h-5 text-gray-400 mx-auto" />
                    ) : feature.premium === "Oui" ? (
                      <Check className="w-5 h-5 text-purple-600 mx-auto" />
                    ) : (
                      <span className="text-purple-600 font-medium">{feature.premium}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto mb-20">
        <h2 className="text-2xl font-bold text-center mb-8">Questions fréquentes</h2>
        
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="font-bold text-lg mb-2">Puis-je changer de plan plus tard ?</h3>
            <p className="text-gray-600">
              Oui, vous pouvez passer du plan Gratuit à Premium à tout moment. Les changements prennent effet immédiatement.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="font-bold text-lg mb-2">Comment les frais sont-ils calculés ?</h3>
            <p className="text-gray-600">
              Sur le plan Gratuit, nous prélevons 18% de chaque transaction. Avec le plan Premium, vous ne payez que votre abonnement mensuel, sans frais supplémentaires sur les transactions.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="font-bold text-lg mb-2">Quels moyens de paiement sont acceptés ?</h3>
            <p className="text-gray-600">
              Nous acceptons les principales méthodes de paiement, y compris les cartes de crédit, Mobile Money et Crypto. Les utilisateurs Premium peuvent également configurer leurs propres solutions de paiement.
            </p>
          </div>
        </div>
      </div>
      
      {/* CTA final */}
      <div className="max-w-3xl mx-auto text-center pb-16">
        <h2 className="text-2xl font-bold mb-4">Prêt à commencer ?</h2>
        <p className="text-gray-600 mb-8">
          Choisissez le plan qui vous convient et commencez à monétiser votre contenu dès aujourd'hui.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => updateVendeur(id, "gratuit")}
            disabled={isUpdating}
            className={`px-8 py-3 rounded-xl bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200 transition-colors flex items-center justify-center ${isUpdating ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isUpdating ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Chargement...
              </>
            ) : 'Essayer gratuitement'}
          </button>
          
          <button
            onClick={() => updateVendeur(id, "payant")}
            disabled={isUpdating}
            className={`px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center justify-center ${isUpdating ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {isUpdating ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Chargement...
              </>
            ) : 'Devenir Premium'}
          </button>
        </div>
      </div>
    </div>
  );
}